import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Code, Callout, Step } from "./primitives.js";

export const meta = {
  id: "dev-add-integration",
  title: "Adding an integration / credential",
  category: "For developers",
  lastUpdated: "2026-05-23",
  description: "Add a new credential card to the Credentials page. Covers field types, Test actions, operator actions, setup guides.",
  relatedGuides: ["dev-architecture", "dev-add-channel"],
  readTimeMin: 5,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        The <AppLink to="credentials">Credentials</AppLink> page is fully driven by the integrations registry. Adding a new credential card is one file + one line. The UI auto-discovers everything from the registered <C>Integration</C> object.
      </Lead>

      <Callout kind="tip">
        Reference implementations in increasing complexity: <C>serpapi.ts</C> (single API key + simple test) → <C>brevo.ts</C> (multiple fields + richer test) → <C>youtube.ts</C> (operator action + setup guide) → <C>anthropic.ts</C> (provider toggle with enum field).
      </Callout>

      <H2>The Integration interface</H2>
      <P>Defined in <C>apps/ui-server/src/integrations/registry.ts</C>:</P>
      <Code>{`interface Integration {
  id: string;                    // URL-safe (used in API routes)
  name: string;                  // operator-facing display name
  description: string;           // one-sentence purpose
  category: "llm" | "search" | "scraping" | "media" | "email"
          | "messaging" | "social" | "cms" | "infra";
  docsUrl?: string;              // external "Get keys" link
  fields: FieldSpec[];           // env var fields
  test?: (values) => Promise<SmokeTestResult>;
  actions?: IntegrationAction[]; // long-running operator buttons
  setupGuide?: SetupGuide;       // collapsible setup instructions
}`}</Code>

      <H2>FieldSpec types</H2>
      <Code>{`interface FieldSpec {
  key: string;                    // env var name (.env compatible)
  label: string;                  // operator-facing label
  description?: string;           // shown below the input
  required?: boolean;             // First required field = "configured?" trigger
  sensitive?: boolean;            // password input + masked; default true
  placeholder?: string;
  validator?: "url" | "email" | "number";
  options?: Array<{               // when set, renders as <select> instead of <input>
    value: string;
    label: string;
    description?: string;
  }>;
}`}</Code>
      <P>Conventions:</P>
      <Ul>
        <Li><strong>sensitive: false</strong> for things like region names, project ids, public account names — exposes plaintext in the UI for easy review.</Li>
        <Li><strong>sensitive: true (default)</strong> for tokens / secrets / API keys — masked as <C>***</C>.</Li>
        <Li><strong>options[]</strong> for enums (provider toggle, region selector, language code). The UI renders a real <C>&lt;select&gt;</C> with the saved value pre-selected.</Li>
        <Li><strong>required: true</strong> drives the "Configured" pill on the card. First required field is the "primary identifier".</Li>
      </Ul>

      <H2>Step-by-step</H2>

      <Step n={1} title="Create the file">
        <P><C>apps/ui-server/src/integrations/&lt;id&gt;.ts</C>:</P>
        <Code>{`import type { Integration } from "./registry.js";

export const myIntegration: Integration = {
  id: "myservice",
  name: "My Service",
  description: "What this does for AlternaBe in one sentence.",
  category: "messaging",  // pick the right bucket
  docsUrl: "https://...",
  fields: [
    { key: "MYSERVICE_API_KEY", label: "API Key", required: true, sensitive: true },
    // ...
  ],
  test: async (values) => {
    const key = values.MYSERVICE_API_KEY;
    if (!key) return { ok: false, error: "MYSERVICE_API_KEY is not set" };
    // Cheap auth-check call.
    const res = await fetch("https://api.myservice.com/whoami", {
      headers: { Authorization: \`Bearer \${key}\` },
    });
    if (!res.ok) return { ok: false, error: \`HTTP \${res.status} \${res.statusText}\` };
    const json = await res.json();
    return { ok: true, summary: \`Authenticated as \${json.user}\` };
  },
};`}</Code>
      </Step>

      <Step n={2} title="Register it">
        <P>In <C>apps/ui-server/src/integrations/registry.ts</C>:</P>
        <Code>{`import { myIntegration } from "./myservice.js";

export const INTEGRATIONS: Integration[] = [
  // ...
  myIntegration,
];`}</Code>
        <P>The position in the array determines display order. Group near similar-category integrations.</P>
      </Step>

      <Step n={3} title="Test action conventions">
        <P>The Test button calls <C>integration.test(values)</C> with current plaintext values. Rules:</P>
        <Ul>
          <Li><strong>Cheap</strong> — &lt;2s. No expensive API calls (no LLM, no large queries). Auth-check endpoints are ideal.</Li>
          <Li><strong>Robust to nulls</strong> — every field could be undefined. Validate before calling.</Li>
          <Li><strong>Operator-friendly errors</strong> — surface "Token expired" / "Wrong project id" / "Bedrock model not enabled" — not raw HTTP statuses.</Li>
          <Li><strong>10-second wallclock cap</strong> — the route layer enforces this automatically.</Li>
        </Ul>
      </Step>

      <Step n={4} title="Add operator actions (optional)">
        <P>For long-running operator-triggered actions (OAuth flows, account migrations, manual syncs), use <C>actions[]</C>:</P>
        <Code>{`actions: [{
  id: "authenticate",
  label: "Authenticate",
  description: "Starts OAuth consent flow...",
  requiresFields: ["MYSERVICE_CLIENT_ID", "MYSERVICE_CLIENT_SECRET"],
  timeoutMs: 180_000,  // 3min default
  run: async (values) => {
    // Long-running operation here. Same return shape as test().
  },
}]`}</Code>
        <P>The card renders the action as a button next to Test. It's auto-disabled until every <C>requiresFields</C> key is saved. Reference: YouTube's "Authenticate" action.</P>
      </Step>

      <Step n={5} title="Add a setup guide (recommended for non-trivial integrations)">
        <P>The Help Center pattern but per-integration. Each guide has sections, each section has steps. Steps support text + optional code block + optional external link.</P>
        <Code>{`setupGuide: {
  intro: "One-paragraph overview of the setup.",
  sections: [
    {
      title: "1. Create a Developer account",
      subtitle: "one-time, outside the app",
      steps: [
        {
          text: "Open the {{PLACEHOLDER}} console and...",
          link: { label: "myservice.com", url: "https://..." }
        },
        {
          text: "Generate an API key. Save it for step 3.",
          code: "curl ..."  // optional
        },
      ],
    },
    // ...3-5 sections is the norm...
  ],
}`}</Code>
        <P><strong>Placeholder substitution</strong>: text + code support <C>&#123;&#123;PLACEHOLDER&#125;&#125;</C> tokens replaced server-side. Register substitutions in <C>resolveSetupGuidePlaceholders()</C> in <C>apps/ui-server/src/routes/integrations.ts</C>. Used today for <C>&#123;&#123;REDIRECT_URI&#125;&#125;</C> (YouTube) + <C>&#123;&#123;MCP_PATH_EXAMPLE&#125;&#125;</C> (OS-appropriate path).</P>
      </Step>

      <Step n={6} title="Update .env.example">
        <P>Add the new env vars at the bottom of <C>.env.example</C> with a one-line comment per var. This is what new contributors copy.</P>
      </Step>

      <Step n={7} title="Update the Help Center">
        <P>Per the help-center maintenance memory: bump <C>lastUpdated</C> on the Credentials reference guide (it auto-pulls live data so usually no code change needed) + the relevant workflow guide (e.g. <C>distribute-article.tsx</C> if it's a distribution channel).</P>
      </Step>

      <H2>Common patterns</H2>
      <Ul>
        <Li><strong>Sensitive boolean</strong> — when an integration needs hot-swap (e.g. flip provider), the credentials store auto-syncs <C>process.env</C> on write. No restart needed.</Li>
        <Li><strong>Two integrations sharing fields</strong> — fine. Each integration declares its own list. The .env file holds one value per key, integrations read what they need.</Li>
        <Li><strong>Multi-step external OAuth</strong> — until we ship server-driven OAuth, paste the resulting tokens into manual-paste fields. See TikTok's pattern (5 fields including expires_at).</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
